import { fmtYm, SETTLEMENT_LABELS } from "./settlementFormat.js";
import { CHART_PALETTE } from "./chartTheme.js";

const ENERGY_TREND_LABELS = { Elec: "전기", Gas: "가스", Water: "수도" };

const STATUS_RANK = { FINAL: 0, DRAFT: 1 };

/** (regYm, energySource)당 FINAL 우선, 동순위면 settlementIdx 큰 것 */
function pickHistoryRowPerYmEnergy(rows) {
    const map = new Map();
    for (const r of rows || []) {
        const energy = String(r.energySource || "");
        if (!energy) continue;
        const key = `${String(r.regYm)}|${energy}`;
        const existing = map.get(key);
        if (!existing) {
            map.set(key, r);
            continue;
        }
        const rankA = STATUS_RANK[r.status] ?? 2;
        const rankB = STATUS_RANK[existing.status] ?? 2;
        if (
            rankA < rankB ||
            (rankA === rankB && Number(r.settlementIdx || 0) > Number(existing.settlementIdx || 0))
        ) {
            map.set(key, r);
        }
    }
    return map;
}

/** ALL 모드: 에너지원별 월별 총요금 추이 (스택 바) */
export function historyToAmountTrendByEnergy(rows, energies = ["Elec", "Gas", "Water"]) {
    const picked = pickHistoryRowPerYmEnergy(rows);
    const byEnergyYm = new Map();
    const ymSet = new Set();

    for (const r of picked.values()) {
        const ym = String(r.regYm);
        const energy = String(r.energySource || "");
        ymSet.add(ym);
        if (!byEnergyYm.has(energy)) byEnergyYm.set(energy, new Map());
        byEnergyYm.get(energy).set(ym, Number(r.kepcoTotalAmount || 0));
    }

    const labelsYm = [...ymSet].sort((a, b) => a.localeCompare(b));
    const series = (energies || [])
        .filter((e) => ENERGY_TREND_LABELS[e])
        .map((e) => ({
            name: ENERGY_TREND_LABELS[e],
            role: "stack",
            color: CHART_PALETTE.energy[e],
            data: labelsYm.map((ym) => {
                const v = byEnergyYm.get(e)?.get(ym);
                return v != null ? v : 0;
            }),
        }));

    return {
        labels: labelsYm.map((ym) => fmtYm(ym)),
        series,
    };
}

/** KPI 스파크라인: 에너지별 월별 총요금 */
export function historyEnergyAmountSpark(rows, energy) {
    const picked = pickHistoryRowPerYmEnergy(rows);
    const points = [];
    for (const r of picked.values()) {
        if (String(r.energySource) !== String(energy)) continue;
        points.push({ regYm: String(r.regYm), amount: Number(r.kepcoTotalAmount || 0) });
    }
    points.sort((a, b) => a.regYm.localeCompare(b.regYm));
    return points.map((p) => p.amount);
}

/**
 * 단일 에너지 월별 청구 추이
 * 총요금 ≈ 사용량 배분 합 + 공용부 배분 (전기)
 */
export function historyToAmountTrend(rows, options = {}) {
    const { showCommonPool = false } = options;
    const { chart: C } = SETTLEMENT_LABELS;
    const sorted = [...(rows || [])].sort((a, b) => String(a.regYm).localeCompare(String(b.regYm)));
    const series = [
        {
            name: C.totalAmount,
            role: "primary",
            data: sorted.map((r) => Number(r.kepcoTotalAmount || 0)),
        },
        {
            name: SETTLEMENT_LABELS.usageShareSum,
            role: "secondary",
            data: sorted.map((r) => Number(r.householdSumIndividual || 0)),
        },
    ];
    if (showCommonPool) {
        series.push({
            name: C.areaShareAmount,
            role: "secondary",
            data: sorted.map((r) => Number(r.commonPoolAmount || 0)),
        });
    }
    return {
        labels: sorted.map((r) => fmtYm(r.regYm)),
        series,
    };
}

/** 월별 사용량 추이 — 총 / 세대 / 공용 라인 (전기: M≈S+C) */
export function historyToUsageTrend(rows, options = {}) {
    const { hideCommon = false } = options;
    const { chart: C } = SETTLEMENT_LABELS;
    const sorted = [...(rows || [])].sort((a, b) => String(a.regYm).localeCompare(String(b.regYm)));

    const series = [
        {
            name: C.totalUsage,
            role: "primary",
            data: sorted.map((r) => Number(r.masterUsageQty || 0)),
        },
        {
            name: C.householdUsage,
            role: "secondary",
            data: sorted.map((r) => Number(r.subUsageSumQty || 0)),
        },
    ];
    if (!hideCommon) {
        series.push({
            name: C.commonUsage,
            role: "secondary",
            data: sorted.map((r) => Number(r.commonUsageQty || 0)),
        });
    }
    return {
        labels: sorted.map((r) => fmtYm(r.regYm)),
        series,
    };
}

/** YYYYMMDD → MM-DD (일별 차트 X축) */
export function fmtDailyAxisLabel(regDt) {
    const s = String(regDt || "").replace(/\D/g, "");
    if (s.length >= 8) return `${s.slice(4, 6)}-${s.slice(6, 8)}`;
    return String(regDt || "");
}

/** tb_summary_day rows → 일별 사용량 차트 */
export function dailyUsageToChart(rows) {
    const sorted = [...(rows || [])].sort((a, b) => String(a.regDt).localeCompare(String(b.regDt)));
    return {
        labels: sorted.map((r) => fmtDailyAxisLabel(r.regDt)),
        data: sorted.map((r) => Number(r.ptVal || 0)),
    };
}
